import { Box, Button, Paper, Slide, Stack, Typography } from '@mui/material';

import Locale from '/b2b/common/components/Locale';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import styles from './StickySaveBar.styles';

const useStyles = makeStyles()(styles);

/**
 * Floating "sticky save" bar pinned to the bottom of the viewport. Shown while a
 * form has edits pending: it slides up + fades in and offers a primary Save
 * action plus an optional secondary Discard action.
 *
 * Purely presentational — dirty tracking, revert and submit are owned by the
 * consumer. Copy is fully parameterized (locale paths) and positioning defaults
 * to viewport-centred; a consumer can offset it per layout (e.g. for a fixed
 * sidebar) by passing `className` onto the outer wrapper.
 *
 * @param {object} props
 * @param {boolean} [props.open] Whether the bar is shown (drives the slide).
 * @param {boolean} [props.canSave] Enables the Save button. Defaults to true.
 * @param {() => void} [props.onSave] Save handler.
 * @param {() => void} [props.onDiscard] Discard handler; the Discard button is
 *   omitted when not provided.
 * @param {string} [props.title] Locale path rendered bold before the message.
 * @param {string} [props.message] Locale path for the body copy.
 * @param {string} [props.discardLabel] Locale path for the Discard button.
 * @param {string} [props.saveLabel] Locale path for the Save button.
 * @param {Record<string, any>} [props.translationOptions] Interpolation values
 *   passed to every Locale lookup.
 * @param {boolean} [props.showIndicator] Show the pulsing dot. Defaults to true.
 * @param {string} [props.className] Extra class merged onto the outer wrapper
 *   (use to override positioning).
 * @param {string} [props.dataTestId] `data-testid` for the wrapper.
 */
const StickySaveBar = props => {
    const {
        open,
        canSave = true,
        onSave,
        onDiscard,
        title,
        message,
        discardLabel = 'common.cancel',
        saveLabel = 'common.save',
        translationOptions,
        showIndicator = true,
        className,
        dataTestId = 'sticky-save-bar',
    } = props;
    const { classes, cx } = useStyles(undefined, {
        props,
    });

    return (
        <Slide direction="up" in={open} appear mountOnEnter unmountOnExit>
            <Box className={cx(classes.root, className)} data-testid={dataTestId}>
                <Paper className={classes.bar} elevation={6}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {showIndicator && (
                            <Box component="span" className={classes.dot} aria-hidden="true" />
                        )}
                        {(title || message) && (
                            <Typography className={classes.text} variant="body2">
                                {title && (
                                    <strong>
                                        <Locale path={title} options={translationOptions} />
                                    </strong>
                                )}
                                {title && message ? ' ' : null}
                                {message && <Locale path={message} options={translationOptions} />}
                            </Typography>
                        )}
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        className={classes.actions}
                    >
                        {onDiscard && (
                            <Button color="primary" variant="text" onClick={onDiscard}>
                                <Locale path={discardLabel} options={translationOptions} />
                            </Button>
                        )}
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={!canSave}
                            onClick={onSave}
                        >
                            <Locale path={saveLabel} options={translationOptions} />
                        </Button>
                    </Stack>
                </Paper>
            </Box>
        </Slide>
    );
};

StickySaveBar.propTypes = {
    canSave: PropTypes.bool,
    className: PropTypes.string,
    dataTestId: PropTypes.string,
    discardLabel: PropTypes.string,
    message: PropTypes.string,
    onDiscard: PropTypes.func,
    onSave: PropTypes.func,
    open: PropTypes.bool,
    saveLabel: PropTypes.string,
    showIndicator: PropTypes.bool,
    title: PropTypes.string,
    translationOptions: PropTypes.object,
};

export default StickySaveBar;
